<script lang="ts">
  import autosize from 'autosize'
  import { pick } from 'lodash-es'
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { normalizeError } from '#app/lib/error'
  import { icon } from '#app/lib/icons'
  import { getSomeColorHexCodeSuggestion } from '#app/lib/images/images'
  import { wait } from '#app/lib/promises'
  import { commands } from '#app/radio'
  import Spinner from '#components/spinner.svelte'
  import { askConfirmation } from '#general/lib/confirmation_modal'
  import VisibilitySelector from '#inventory/components/visibility_selector.svelte'
  import type { ControllersAPI } from '#server/types/controllers_api'
  import { createShelf, updateShelf, deleteShelf, type SerializedShelf } from '#shelves/lib/shelves'
  import { i18n, I18n } from '#user/lib/i18n'
  import { updateMainUserShelvesCount } from '#user/lib/serialized_main_user'

  let {
    shelf = $bindable(),
    onShelfEditorDone,
  }: {
    shelf?: SerializedShelf
    onShelfEditorDone: (shelf: SerializedShelf) => void
  } = $props()

  const isNewShelf = shelf == null

  const update = $state(isNewShelf ?
    {
      name: '',
      description: '',
      visibility: [],
      color: getSomeColorHexCodeSuggestion(),
    } as ControllersAPI['shelves']['post']['default']['params'] :
      pick(shelf, [ 'name', 'description', 'visibility', 'color' ]) as ControllersAPI['shelves']['put']['default']['params'],
  )

  let flash: FlashState | undefined = $state()
  let waiting: Promise<SerializedShelf> | undefined = $state()

  async function validate () {
    try {
      if (isNewShelf) {
        waiting = createShelf(update as ControllersAPI['shelves']['post']['default']['params'])
        shelf = await waiting
        updateMainUserShelvesCount(1)
      } else {
        waiting = updateShelf({ id: shelf._id, ...update })
        await waiting
        shelf = { ...shelf, ...update }
      }
      flash = { type: 'success', message: I18n('saved') }
      await wait(800)
      onShelfEditorDone(shelf)
    } catch (err) {
      flash = normalizeError(err)
    }
  }

  async function askShelfDeletionConfirmation () {
    askConfirmation({
      confirmationText: I18n('delete_shelf_confirmation', { name }),
      warningText: I18n('cant_undo_warning'),
      action: _deleteShelf,
    })
  }

  async function _deleteShelf () {
    try {
      await deleteShelf({ ids: [ shelf._id ] })
      updateMainUserShelvesCount(-1)
      commands.execute('show:inventory:main:user')
    } catch (err) {
      flash = normalizeError(err)
    }
  }
</script>

<div class="shelf-editor" role="dialog" aria-labelledby="shelf-editor-title">
  <h3 id="shelf-editor-title">
    {#if isNewShelf}
      {I18n('create a new shelf')}
    {:else}
      {I18n('edit shelf')}
    {/if}
  </h3>

  <label>
    {i18n('name')}
    <input
      type="text"
      bind:value={update.name}
      required
      use:autofocus
    />
  </label>

  <label>
    {i18n('description')}
    <textarea type="text" bind:value={update.description} use:autosize></textarea>
  </label>

  <VisibilitySelector bind:visibility={update.visibility} showTip={true} />

  <label>
    {i18n('color')}
    <input type="color" bind:value={update.color} />
  </label>

  <Flash bind:state={flash} />

  <div class="buttons">
    {#await waiting}
      <Spinner />
    {:then}
      {#if !flash}
        {#if !isNewShelf}
          <button class="delete button" onclick={askShelfDeletionConfirmation}>
            {@html icon('trash')}
            {I18n('delete')}
          </button>
        {/if}
        <button
          class="validate button success-button"
          title={update.name === '' ? i18n('A shelf name is required') : I18n('validate')}
          disabled={update.name === ''}
          onclick={validate}
        >
          {@html icon('check')}
          {#if isNewShelf}
            {I18n('create')}
          {:else}
            {I18n('validate')}
          {/if}
        </button>
      {/if}
    {/await}
  </div>
</div>

<style lang="scss">.shelf-editor {
  padding: 0 0.5em;
}
.shelf-editor label, .shelf-editor :global(fieldset) {
  margin: 1em 0;
}

label {
  font-size: 1rem;
  margin-block-end: 0.2em;
  cursor: pointer;
}

input[type=color] {
  display: block;
  margin: 0.5rem;
}

h3 {
  text-align: center;
}

.buttons {
  margin-block-start: 1em;
  align-self: stretch;
}

.delete, .validate {
  text-align: center;
  font-weight: bold;
  padding: 0.7em;
  flex: 1;
}

.delete {
  transition: background-color 0.2s 0s ease;
  background-color: #636466;
  color: white;
}
.delete:hover:not(:disabled), .delete:focus:not(:disabled) {
  color: white;
  background-color: #ff4e4e;
}

/* Very small screens */
@media screen and (width < 350px) {
  .shelf-editor label, .shelf-editor :global(fieldset) {
    padding: 0 0.5em;
  }
  .buttons {
    align-self: stretch;
    flex: 1;
    padding: 1em;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
  }
  .buttons button {
    min-width: 12em;
    max-width: 20em;
    margin: 0.5em auto;
  }
}
/* Large screens */
@media screen and (width >= 350px) {
  .buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }
  .delete {
    margin-inline-end: 0.5em;
  }
  .validate {
    margin-inline-start: 0.5em;
  }
}</style>
